'use client'

import { useTransition } from 'react'
import { usePathname, useRouter } from 'next/navigation'

import { LoadingOverlay } from '@/app/components/shared/LoadingOverlay'
import { LocationPicker } from '@/app/components/shared/LocationPicker'
import { setManualLocation } from '@/app/lib/geolocation'
import { invalidateResolvedTargetCache } from '@/app/lib/hooks/useGeoLocationSync'
import { buildLocalizedPath } from '@/app/lib/routing'
import { cx } from '@/app/lib/utils'

import { DATE_RANGE_TABS, KICKER_LABEL, LOCATION_CHANGE_LOADING_LABEL } from './FinDeSemana.constants'
import type { DateRangeValue } from './FinDeSemana.types'
import styles from './FinDeSemana.module.scss'

interface FinDeSemanaHeaderProps {
  readonly headingId: string
  readonly dateRange: DateRangeValue
  readonly provinceNicename: string
  readonly onDateRangeChange: (value: DateRangeValue) => void
}

/**
 * Cabecera del módulo `FinDeSemana`: antetítulo fijo "Recomendados", título
 * calculado ("{chip activa} en {provincia}", spec.md FR-007) y una fila de 5
 * chips de periodo con forma cuadrada (Figma node 5735-20960 — a propósito
 * distintas del átomo `Chip` compartido, que es pill; ver botones locales en
 * `FinDeSemana.module.scss`).
 *
 * El selector de provincia **reutiliza el mismo comportamiento que
 * `ProductosLocalizadosHeader`/`AgendaHeader`** (corrección de negocio): al
 * elegir una provincia persiste la elección (`setManualLocation`) y navega
 * -sin recarga completa, sin perder la posición de scroll- a la variante
 * localizada de la página anfitriona cuando esa página tiene un segmento de
 * localización addressable (`buildLocalizedPath`); si no lo tiene, solo
 * persiste la cookie. Mientras dura esa navegación se muestra el
 * `LoadingOverlay` general del sitio (mismo componente/patrón que
 * `ProductosLocalizadosHeader`).
 *
 * Las chips de periodo, en cambio, disparan un refetch en segundo plano
 * propio de este módulo (`FinDeSemanaClient`, sin navegar) — su indicador de
 * carga (el mismo `LoadingOverlay` general) lo muestra ese componente, no
 * este.
 */
export function FinDeSemanaHeader({
  headingId,
  dateRange,
  provinceNicename,
  onDateRangeChange,
}: FinDeSemanaHeaderProps) {
  const router = useRouter()
  const pathname = usePathname()
  const [isNavigating, startTransition] = useTransition()

  const activeLabel = DATE_RANGE_TABS.find((tab) => tab.value === dateRange)?.label ?? ''

  async function handleProvinceSelect(newLocation: string) {
    // `await` + invalidar la caché: ver `AgendaHeader`.
    await setManualLocation(newLocation)
    invalidateResolvedTargetCache()

    const nextPath = buildLocalizedPath(pathname, provinceNicename, newLocation)
    if (nextPath === null) return

    // Leído en el handler, no con `useSearchParams()`: ese hook sin `<Suspense>`
    // rompe el prerender/ISR de la página anfitriona (BAILOUT_TO_CLIENT_SIDE_RENDERING).
    const qs = window.location.search.slice(1)
    startTransition(() => {
      // `scroll: false`: mantiene la posición de scroll — el módulo debe
      // seguir en la vista tras el cambio de provincia.
      router.push(qs ? `${nextPath}?${qs}` : nextPath, { scroll: false })
    })
  }

  return (
    <div className={styles.dataContent}>
      {isNavigating && <LoadingOverlay label={LOCATION_CHANGE_LOADING_LABEL} />}
      <p className={styles.kicker}>{KICKER_LABEL}</p>
      <h2 id={headingId} className={styles.title}>
        {activeLabel} en{' '}
        <LocationPicker location={provinceNicename} onSelect={handleProvinceSelect} inverse />
      </h2>

      <ul className={styles.chips} aria-label="Periodo">
        {DATE_RANGE_TABS.map((tab) => {
          const isSelected = tab.value === dateRange
          return (
            <li key={tab.value} className={styles.chipItem}>
              <button
                type="button"
                className={cx(styles.chipButton, isSelected && styles.chipButtonSelected)}
                aria-pressed={isSelected}
                onClick={() => onDateRangeChange(tab.value)}
              >
                {tab.label}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
